import React from 'react';
import { Leaf, Bird, Trees, Bug } from 'lucide-react';

const WildlifeInfo = () => {
  const wildlifeData = [
    {
      id: 'overview',
      title: 'Flora & Fauna Overview',
      icon: Trees,
      stats: [
        { label: 'Terrestrial Vertebrate Species', value: '269' },
        { label: 'Endemic Species in PPSRNP', value: '70', detail: '11% of 637 endemic species in Palawan' },
        { label: 'Endemic Fauna', value: '38', detail: '24% of 158 fauna recorded' }
      ],
      description: 'The PPSRNP contains moderate faunal diversity with 269 terrestrial vertebrate species recorded thriving within its bounds. Of the 637 endemic flora and fauna documented in Palawan, 70 species (11%) is reported present in our PPSRNP. Thirty eight (24%) of the 158 fauna recorded in PPSRNP are endemic species.'
    },
    {
      id: 'birds',
      title: 'Avian Diversity',
      icon: Bird,
      image: '/assets/images/UNDERGROUND_-_6-1769151502955.jpg',
      imageAlt: 'Endemic bird species in natural forest habitat within PPSRNP',
      description: 'Birds comprise the largest group of vertebrates found in the park. 5 threatened ave-fauna listed under the Convention of International Trade in Endangered Species of Wild Fauna and Flora (CITES) can also be found in the park.',
      highlight: 'Largest vertebrate group in PPSRNP'
    },
    {
      id: 'reptiles',
      title: 'Reptilian Species',
      icon: Bug,
      image: '/assets/images/A038A259-FC75-4E82-B1CE-DBFAF08EF619-1765250829061.png',
      imageAlt: 'Monitor lizard and other reptile species in PPSRNP rocky habitat',
      description: '19 species of reptiles are also present in the park. 8 of which are endemic.',
      stats: [
        { label: 'Total Reptile Species', value: '19' },
        { label: 'Endemic Reptiles', value: '8' }
      ]
    },
    {
      id: 'amphibians',
      title: 'Amphibian Life',
      icon: Leaf,
      image: '/assets/images/UNDERGROUND_-_6-1769151502955.jpg',
      imageAlt: 'Amphibian species in wetland habitat within PPSRNP',
      description: 'With 10 species recorded, Amphibians are the smallest group of vertebrates that inhabits within the park.',
      stats: [
        { label: 'Amphibian Species', value: '10' }
      ]
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-[var(--color-card)] to-[var(--color-background)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-[var(--color-primary)] dark:text-[var(--color-tertiary)] mb-4">
            PPSRNP Wildlife
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
            Discover the rich biodiversity of flora and fauna that call Puerto Princesa Subterranean River National Park their home.
          </p>
        </div>

        {/* Wildlife Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {wildlifeData?.map((item) => {
            const IconComponent = item?.icon;
            return (
              <div
                key={item?.id}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-[var(--color-border)]"
              >
                {/* Image (if available) */}
                {item?.image && (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={item?.image}
                      alt={item?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  </div>
                )}
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[var(--color-accent)]/20 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-[var(--color-accent)] dark:text-[var(--color-tertiary)]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[var(--color-primary)] dark:text-[var(--color-tertiary)]">
                      {item?.title}
                    </h3>
                  </div>

                  {item?.highlight && (
                    <div className="mb-3">
                      <span className="inline-block px-3 py-1 bg-[var(--color-success)]/20 text-[var(--color-success)] dark:text-[var(--color-tertiary)] rounded-full text-sm font-semibold">
                        {item?.highlight}
                      </span>
                    </div>
                  )}

                  <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                    {item?.description}
                  </p>

                  {item?.stats && item?.stats?.length > 0 && (
                    <div className="grid grid-cols-1 gap-3 mt-4">
                      {item?.stats?.map((stat, idx) => (
                        <div key={idx} className="bg-[var(--color-card)] dark:bg-gray-700 rounded-lg p-3">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-[var(--color-primary)] dark:text-[var(--color-tertiary)] mb-1">
                              {stat?.value}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                              {stat?.label}
                            </div>
                            {stat?.detail && (
                              <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                {stat?.detail}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WildlifeInfo;